// src/pages/userinvite.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Login.css';

const API_URL = import.meta.env.VITE_API_URL;

// ─── 共通ロジック ────────────────────────────────────────────────────────────

/**
 * accept-member / accept-user の結果を見て適切な通知ページへ遷移する。
 * 成功した場合は true、失敗した場合は false を返す。
 */
const redirectAfterAccept = (attachRes, navigate) => {
  if (!attachRes.data?.success) return false;
  const dest = attachRes.data.created
    ? '/auth/login-view/userinvite'         // 新規で紐づけ完了
    : '/auth/login-view/userinvite-already'; // すでに紐づき済み
  navigate(dest, { replace: true });
  return true;
};

/**
 * ログイン or 新規登録後、purpose に合わせた参加 API を呼んでリダイレクトする。
 * エラーメッセージを返す（成功時は null）。
 */
const processPurpose = async (purpose, token, navigate, access) => {
  const headers = { Authorization: `Bearer ${access}` };
  if (purpose === 'invite_link_member') {
    const res = await axios.post(`${API_URL}/api/auth/userinvite/accept-member/`, { token }, { headers });
    if (!redirectAfterAccept(res, navigate)) {
      return res.data?.message || '参加処理に失敗しました。';
    }
  } else if (purpose === 'invite_link_user') {
    const res = await axios.post(`${API_URL}/api/auth/userinvite/accept-user/`, { token }, { headers });
    if (!redirectAfterAccept(res, navigate)) {
      return res.data?.message || '参加処理に失敗しました。';
    }
  } else {
    return '無効な招待です。';
  }
  return null; // 成功
};

/** API エラーをユーザー向けメッセージに変換する */
const parseApiError = (err) => {
  const status = err?.response?.status;
  const msg =
    err?.response?.data?.message ??
    err?.response?.data?.error ??
    '通信エラーが発生しました。';

  if (status === 409) return 'このユーザーIDは既に登録されています。';
  if (status === 429) return '操作が集中しています。しばらくしてからお試しください。';
  if (status === 403) return 'CSRFエラーが発生しました。ページを再読み込みしてやり直してください。';
  return msg;
};

/** OAuth の state パラメータを base64 エンコードする */
const encodeOAuthState = (payload) => {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
  } catch {
    return '';
  }
};

// ─── メインコンポーネント ────────────────────────────────────────────────────

export default function UserInvite() {
  const [phase, setPhase]                 = useState('checking'); // 'checking' | 'form' | 'error'
  const [errorText, setErrorText]         = useState('');
  const [activeTab, setActiveTab]         = useState('login');    // 'login' | 'signup'
  const [invitePurpose, setInvitePurpose] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('t') ?? '';

  // タブ初期値を location.state から復元（OTP 検証後の戻りで 'signup' を開く）
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  // OAuth コールバック後の処理（?from=oauth のとき）
  useEffect(() => {
    const isOAuthReturn = new URLSearchParams(location.search).get('from') === 'oauth';
    if (!isOAuthReturn || !token) return;

    const handleOAuthReturn = async () => {
    try {
      const inv = await axios.post(`${API_URL}/api/auth/userinvite/validate/`, { token });
      if (!inv.data?.valid) {
        return navigate('/auth/login-view/userinvite-error', { replace: true });
      }

      // JWTをヘッダーに乗せて processPurpose を呼ぶ
      const { access } = res.data;
      const error = await processPurpose(inv.data.purpose, token, navigate, access);
      if (error) {
        navigate('/auth/login-view/userinvite-error', { replace: true });
      }
    } catch {
      navigate('/auth/login-view/userinvite-error', { replace: true });
    }
  };

    handleOAuthReturn();
  }, [token, location, navigate]);

  // 招待トークンの初期検証
  useEffect(() => {
    if (!token) {
      setPhase('error');
      setErrorText('招待トークンが見つかりません。');
      return;
    }

    const verifyToken = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/userinvite/validate/`, { token });
        if (res.data?.valid) {
          setInvitePurpose(res.data.purpose ?? null);
          setPhase('form');
        } else {
          setPhase('error');
          setErrorText('無効な招待トークンです。');
        }
      } catch {
        setPhase('error');
        setErrorText('通信エラーが発生しました。');
      }
    };

    verifyToken();
  }, [token]);

  if (phase === 'checking') {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2 className="login-title">招待を確認中...</h2>
          <p className="login-subtitle">しばらくお待ちください。</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2 className="login-title">招待エラー</h2>
          <div className="error-alert">{errorText}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card" role="dialog" aria-labelledby="invite-title">
        <h2 id="invite-title" className="login-title">ユーザー招待</h2>
        <p className="login-subtitle">既存アカウントまたは新規アカウントで参加できます。</p>

        <TabBar activeTab={activeTab} onChange={setActiveTab} />

        {activeTab === 'login'
          ? <InviteLogin  token={token} purpose={invitePurpose} />
          : <InviteSignup token={token} purpose={invitePurpose} />}
      </div>
    </div>
  );
}

// ─── タブバー ────────────────────────────────────────────────────────────────

function TabBar({ activeTab, onChange }) {
  const tabStyle = (id) => ({
    flex: 1,
    padding: '8px 0',
    background: 'none',
    border: 'none',
    fontWeight: 600,
    cursor: 'pointer',
    color: activeTab === id ? 'var(--text)' : '#6b7280',
    borderBottom: activeTab === id ? '2px solid var(--brand-600)' : '2px solid transparent',
  });

  return (
    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
      <button type="button" style={tabStyle('login')}  onClick={() => onChange('login')}>
        既存アカウント
      </button>
      <button type="button" style={tabStyle('signup')} onClick={() => onChange('signup')}>
        新規アカウント
      </button>
    </div>
  );
}

// ─── 既存アカウントで参加 ────────────────────────────────────────────────────

function InviteLogin({ token, purpose }) {
  const [userAddress, setUserAddress] = useState('');
  const [userPass, setUserPass]       = useState('');
  const [showPass, setShowPass]       = useState(false);
  const [errorText, setErrorText]     = useState('');
  const [submitting, setSubmitting]   = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorText('');

    try {
      const res = await axios.post(`${API_URL}/api/login/`, { userAddress, userPass });
      if (!res.data?.success) {
        setErrorText('メールアドレス・パスワードをご確認ください。');
        return;
      }

      // JWTをヘッダーに乗せて processPurpose を呼ぶ
      const { access } = res.data;
      const error = await processPurpose(purpose, token, navigate, access);
      if (error) setErrorText(error);
    } catch {
      setErrorText('ログイン中にエラーが発生しました。');
    } finally {
      setSubmitting(false);
    }
  };

  const startOAuth = (provider) => {
    const next = `/userinvite?t=${encodeURIComponent(token)}`;
    const state = encodeOAuthState({
      next,
      flow: 'invite',
      token,
      ts: Date.now(),
      nonce: Math.random().toString(36).slice(2),
    });
    window.location.href = `${API_URL}/api/oauth/${provider}/start?state=${encodeURIComponent(state)}`;
  };

  return (
    <form onSubmit={handleLogin} noValidate>
      {errorText && <div className="error-alert" role="alert">{errorText}</div>}

      <div className="form-group">
        <label htmlFor="login-email">メールアドレス</label>
        <input
          id="login-email"
          type="email"
          value={userAddress}
          onChange={(e) => setUserAddress(e.target.value)}
          required
          autoComplete="username"
        />
      </div>

      <PasswordField
        id="login-pass"
        label="パスワード"
        value={userPass}
        onChange={(e) => setUserPass(e.target.value)}
        show={showPass}
        onToggle={() => setShowPass((v) => !v)}
        autoComplete="current-password"
      />

      <button
        type="submit"
        className={`login-button ${submitting ? 'is-loading' : ''}`}
        disabled={submitting}
      >
        {submitting ? '参加中…' : '参加'}
      </button>

      <div className="oauth-divider"><span>または</span></div>
      <div className="oauth-buttons">
        <button type="button" className="oauth-btn oauth-google" onClick={() => startOAuth('google')}>
          Googleで参加
        </button>
        <button type="button" className="oauth-btn oauth-line" onClick={() => startOAuth('line')}>
          LINEで参加
        </button>
      </div>
      <p style={{ color: '#888', marginTop: 10, textAlign: 'center', fontSize: 13 }}>
        GoogleまたはLINEアカウントにて新規ログインの場合、<br />
        メールアドレスを利用しアカウントを作成いたします
      </p>
    </form>
  );
}

// ─── 新規アカウントで参加 ────────────────────────────────────────────────────

function InviteSignup({ token, purpose }) {
  const [userName,    setUserName]    = useState('');
  const [userAddress, setUserAddress] = useState('');
  const [userPass,    setUserPass]    = useState('');
  const [userPass2,   setUserPass2]   = useState('');
  const [showPass1,   setShowPass1]   = useState(false);
  const [showPass2,   setShowPass2]   = useState(false);
  const [errorText,   setErrorText]   = useState('');
  const [submitting,  setSubmitting]  = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // OTP 検証完了後に戻ってきたとき、sessionStorage のドラフトを使って登録を完了する
  useEffect(() => {
    const state = location.state;
    if (!state?.otpVerified || !state?.email) return;

    const draftRaw = sessionStorage.getItem('signupDraft');
    if (!draftRaw) {
      setErrorText('入力内容が失われました。もう一度やり直してください。');
      return;
    }

    // ← 読んだ直後に即削除（2回目の発火を防ぐ）
    sessionStorage.removeItem('signupDraft');
    sessionStorage.removeItem('otpFlowState');

    try {
      const draft = JSON.parse(draftRaw);
      if (draft.userAddress?.trim().toLowerCase() !== state.email.trim().toLowerCase()) {
        setErrorText('メールアドレスが一致しません。もう一度やり直してください。');
        return;
      }
      finalizeSignup(draft);
    } catch {
      setErrorText('入力内容の復元に失敗しました。もう一度やり直してください。');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);


  /** OTP 検証後：ドラフトを使って実際にユーザー作成 → 参加処理 */
  const finalizeSignup = async (draft) => {
    const { userName: name, userAddress: email, userPass: pass, userPass2: pass2 } = draft;

    if (!name)           return setErrorText('氏名を入力してください。');
    if (!email)          return setErrorText('ユーザーIDを入力してください。');
    if (pass.length < 8) return setErrorText('パスワードは8文字以上にしてください。');
    if (pass !== pass2)  return setErrorText('パスワード（確認）が一致しません。');

    setSubmitting(true);
    setErrorText('');

    try {
      // ユーザー作成 + 組織紐づけ を1回のAPIで完結
      const res = await axios.post(`${API_URL}/api/auth/userinvite/create-and-join/`, {
        userName: name,
        userAddress: email,
        userPass: pass,
        token,           // 招待トークンも一緒に送る
      });

      if (!res.data?.success) {
        setErrorText(res.data?.message || '登録に失敗しました。');
        return;
      }

      localStorage.setItem('lastLoginID', email);
      // 紐づけ結果に応じてリダイレクト
      const dest = res.data.created
        ? '/auth/login-view/userinvite'
        : '/auth/login-view/userinvite-already';
      navigate(dest, { replace: true });

    } catch (err) {
      setErrorText(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  /** 「登録して参加」ボタン：メール重複チェック → OTP 開始 → OTP 画面へ遷移 */
  const handleSignup = async (e) => {
    e.preventDefault();

    if (!userName.trim())       return setErrorText('氏名を入力してください。');
    if (!userAddress.trim())    return setErrorText('ユーザーIDを入力してください。');
    if (userPass.length < 8)    return setErrorText('パスワードは8文字以上にしてください。');
    if (userPass !== userPass2) return setErrorText('パスワード（確認）が一致しません。');

    setSubmitting(true);
    setErrorText('');

    try {
      const email = userAddress.trim();

      // ① メールアドレスの重複チェック
      try {
        const chk = await axios.post(`${API_URL}/api/signup/check`, { userAddress: email });
        if (!chk.data?.available) {
          setErrorText('このユーザーIDは既に登録されています。');
          return;
        }
      } catch (chkErr) {
        if (chkErr?.response?.status === 409) {
          setErrorText('このユーザーIDは既に登録されています。');
        } else {
          setErrorText(parseApiError(chkErr));
        }
        return;
      }

      // ② OTP 開始
      const otpRes = await axios.post(`${API_URL}/api/otp/start`, { userAddress: email, purpose: 'signup' });
      const { challengeId, ttlSec, resendCooldownSec, maskedEmail } = otpRes.data ?? {};
      if (!challengeId) {
        setErrorText('ワンタイムパスの開始に失敗しました。時間をおいて再度お試しください。');
        return;
      }

      // OTP 検証後に戻ってきたときのために入力値を退避
      const returnTo    = `/userinvite?t=${encodeURIComponent(token)}`;
      const returnState = { otpVerified: true, email, activeTab: 'signup' };

      sessionStorage.setItem('signupDraft', JSON.stringify({
        userName: userName.trim(),
        userAddress: email,
        userPass,
        userPass2,
      }));
      sessionStorage.setItem('otpFlowState', JSON.stringify({
        challengeId, userAddress: email, maskedEmail,
        ttlSec, resendCooldownSec, returnTo, returnState,
      }));

      // ③ OTP 検証画面へ（検証後は returnTo に returnState を付けて戻ってくる）
      navigate('/otpverify', {
        state: { challengeId, userAddress: email, maskedEmail, ttlSec, resendCooldownSec, returnTo, returnState },
        replace: true,
      });
    } catch (err) {
      setErrorText(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSignup} noValidate>
      {errorText && <div className="error-alert" role="alert">{errorText}</div>}

      <div className="form-group">
        <label htmlFor="signup-name">氏名</label>
        <input
          id="signup-name"
          type="text"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="signup-email">メールアドレス</label>
        <input
          id="signup-email"
          type="email"
          value={userAddress}
          onChange={(e) => setUserAddress(e.target.value)}
          required
          autoComplete="username"
        />
      </div>

      <PasswordField
        id="signup-pass"
        label="パスワード"
        value={userPass}
        onChange={(e) => setUserPass(e.target.value)}
        show={showPass1}
        onToggle={() => setShowPass1((v) => !v)}
        autoComplete="new-password"
      />

      <PasswordField
        id="signup-pass2"
        label="パスワード（確認）"
        value={userPass2}
        onChange={(e) => setUserPass2(e.target.value)}
        show={showPass2}
        onToggle={() => setShowPass2((v) => !v)}
        autoComplete="new-password"
      />

      <button
        type="submit"
        className={`login-button ${submitting ? 'is-loading' : ''}`}
        disabled={submitting}
      >
        {submitting ? '登録中…' : '登録して参加'}
      </button>
    </form>
  );
}

// ─── 共通 UI パーツ ──────────────────────────────────────────────────────────

function PasswordField({ id, label, value, onChange, show, onToggle, autoComplete }) {
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <div className="password-field">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          required
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="toggle-password"
          onClick={onToggle}
          aria-label={show ? 'パスワードを非表示' : 'パスワードを表示'}
        >
          {show ? '隠す' : '表示'}
        </button>
      </div>
    </div>
  );
}